// =================================================================
// ChatSearch.js - Recherche dans les messages affichés
// Session 34 — Recherche locale, highlight, navigation prev/next
// =================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react';

const ChatSearch = ({ messages, isOpen, onClose, onHighlight }) => {
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    if (!query.trim() || !messages) {
      setMatches([]);
      setCurrentIdx(0);
      onHighlight && onHighlight(null);
      return;
    }
    const q = query.toLowerCase();
    const found = messages
      .map((m, idx) => ({ messageId: m.id, idx, content: m.content || '' }))
      .filter(m => m.content.toLowerCase().includes(q));
    setMatches(found);
    setCurrentIdx(0);
    if (found.length > 0) {
      scrollToMatch(found[0].messageId);
      onHighlight && onHighlight(found[0].messageId);
    } else {
      onHighlight && onHighlight(null);
    }
  }, [query, messages]);

  const scrollToMatch = useCallback((messageId) => {
    const el = document.getElementById(`msg-${messageId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const goNext = () => {
    if (matches.length === 0) return;
    const next = (currentIdx + 1) % matches.length;
    setCurrentIdx(next);
    scrollToMatch(matches[next].messageId);
    onHighlight && onHighlight(matches[next].messageId);
  };

  const goPrev = () => {
    if (matches.length === 0) return;
    const prev = (currentIdx - 1 + matches.length) % matches.length;
    setCurrentIdx(prev);
    scrollToMatch(matches[prev].messageId);
    onHighlight && onHighlight(matches[prev].messageId);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) goPrev();
      else goNext();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '8px 16px', background: 'var(--card-bg)',
      borderBottom: '1px solid var(--card-border)',
      animation: 'fadeIn 0.15s ease',
    }}>
      <Search size={14} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
      <input
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Rechercher dans la conversation..."
        autoFocus
        style={{
          flex: 1, padding: '4px 8px', border: '1px solid var(--card-border)',
          borderRadius: '6px', background: 'var(--input-bg)', color: 'var(--text)',
          fontSize: '13px', outline: 'none',
        }}
      />
      {query && (
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {matches.length > 0 ? `${currentIdx + 1}/${matches.length}` : '0/0'}
        </span>
      )}
      <button onClick={goPrev} disabled={matches.length === 0} title="Précédent"
        style={{ background: 'none', border: 'none', cursor: matches.length ? 'pointer' : 'default', color: 'var(--text-secondary)', padding: '2px' }}>
        <ChevronUp size={14} />
      </button>
      <button onClick={goNext} disabled={matches.length === 0} title="Suivant"
        style={{ background: 'none', border: 'none', cursor: matches.length ? 'pointer' : 'default', color: 'var(--text-secondary)', padding: '2px' }}>
        <ChevronDown size={14} />
      </button>
      <button onClick={() => { setQuery(''); onHighlight && onHighlight(null); onClose(); }} title="Fermer"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '2px' }}>
        <X size={14} />
      </button>
    </div>
  );
};

export default ChatSearch;
